<template>
  <v-row no-gutters>
    <v-col cols="12" class="text-left" @mouseenter="openAllEmptyGroups" @click="openAllEmptyGroups">
      <vuetify-draggable-treeview v-model="computedItems" v-bind="draggableTreeviewBind" v-on="$listeners">
        <template v-for="(value, key) in $scopedSlots" #[key]="scope">
          <v-col
            cols="auto"
            :class="`pa-0 treeview-slot-${key}`"
            align-self="start"
            :key="key"
            :ref="`${key}TreeviewElement${scope.item.uniqueTreeviewKey}${scope.open}`"
          >
            <v-row no-gutters align="start" :ref="`${key}TreeviewNode${scope.item.uniqueTreeviewKey}`">
              <v-col
                cols="auto"
                class="pt-1"
                style="max-height: 32px; max-width: 25px"
                v-if="key === 'prepend' && selectable"
              >
                <v-tooltip bottom color="font" :disabled="!scope.item.selectableTooltip">
                  <template #activator="{ on }">
                    <span v-on="on">
                      <v-checkbox
                        v-model="scope.item.selectableValue"
                        @click.native.stop
                        @change="selectElement(scope.item.uniqueTreeviewKey, $event)"
                        class="pa-0 ma-0"
                        :disabled="scope.item.selectableDisabled"
                        :on-icon="
                          !scope.item.children ||
                          scope.item.children.every(
                            children =>
                              children.selectableValue ||
                              (notSelectableChildrenKey && !children[notSelectableChildrenKey])
                          )
                            ? '$checkboxOn'
                            : 'mdi-minus-box'
                        "
                      />
                    </span>
                  </template>
                  {{ scope.item.selectableTooltip }}
                </v-tooltip>
              </v-col>
              <slot :name="key" v-bind="scope" />
            </v-row>
            <v-row no-gutters v-if="scope.item.children && scope.item.children.length < 1" class="no-elements">
              <v-col cols="auto" class="pl-1" v-if="key === 'label'">
                {{ $i18n.t('admin.noLayers') }}
              </v-col>
            </v-row>
          </v-col>
        </template>
      </vuetify-draggable-treeview>
    </v-col>
  </v-row>
</template>
<script>
import { debounce } from 'lodash';
import VuetifyDraggableTreeview from 'vuetify-draggable-treeview';
export default {
  name: 'DraggableTreeview',
  components: { VuetifyDraggableTreeview },
  props: {
    expandIcon: {
      type: String,
      default: 'mdi-menu-down',
    },
    groupNameKey: {
      type: String,
      default: 'name',
    },
    itemChildren: {
      type: String,
      default: 'children',
    },
    itemNameKey: {
      type: String,
      default: 'name',
    },
    items: {
      type: Array,
      default: () => {
        return [];
      },
    },
    notSelectableChildrenKey: {
      type: String,
    },
    onlyChildrenDraggable: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selectableKey: {
      type: String,
      default: 'value',
    },
  },
  computed: {
    computedItems: {
      get() {
        let items = JSON.parse(JSON.stringify(this.items));
        let uniqueTreeviewKey = 0;
        items = items.map(item => {
          uniqueTreeviewKey++;
          item.uniqueTreeviewKey = `utk-${uniqueTreeviewKey}`;
          if (!item.id && item.id !== 0) {
            item.id = `utk-${uniqueTreeviewKey}`;
          }
          if (item[this.itemChildren]) {
            item.children = item[this.itemChildren].map(children => {
              if (this.selectable) {
                children.selectableValue = children[this.selectableKey];
              }
              children.name = children[this.itemNameKey];
              uniqueTreeviewKey++;
              const id = children.id || children.id === 0 ? children.id : `utk-${uniqueTreeviewKey}`;
              return { ...children, uniqueTreeviewKey: `utk-${uniqueTreeviewKey}`, id };
            });
            if (this.selectable) {
              item.selectableValue = item.children.find(children => children.selectableValue) ? true : false;
            }
            item.name = item[this.groupNameKey];
          } else {
            item.name = item[this.itemNameKey];
            if (this.selectable) {
              item.selectableValue = item[this.selectableKey];
            }
          }
          return item;
        });
        return items;
      },
      set(nV) {
        let items = JSON.parse(JSON.stringify(nV));
        items = items.map(item => {
          if (item.children) {
            item[this.itemChildren] = item.children.map(children => {
              if (this.selectable && this.selectableKey !== 'selectableValue') {
                children[this.selectableKey] = children.selectableValue;
                delete children.selectableValue;
              }
              if (this.itemNameKey !== 'name') {
                children[this.itemNameKey] = children.name;
                delete children.name;
              }
              delete children.uniqueTreeviewKey;
              if (typeof children.id === 'string' && children.id.includes('utk-')) {
                delete children.id;
              }
              return children;
            });
            if (this.groupNameKey !== 'name') {
              item[this.groupNameKey] = item.name;
              delete item.name;
            }
            if (this.itemChildren !== 'children') {
              delete item.children;
            }
            if (this.selectable && this.selectableKey !== 'selectableValue') {
              delete item.selectableValue;
            }
          } else {
            if (this.itemNameKey !== 'name') {
              item[this.itemNameKey] = item.name;
              delete item.name;
            }
            if (this.selectable && this.selectableKey !== 'selectableValue') {
              item[this.selectableKey] = item.selectableValue;
              delete item.selectableValue;
            }
          }
          delete item.uniqueTreeviewKey;
          if (typeof item.id === 'string' && item.id.includes('utk-')) {
            delete item.id;
          }
          return item;
        });
        this.$emit('update:items', items);
      },
    },
    computedLayers() {
      return this.$_flattenStructure(this.computedItems, 'children');
    },
    computedGroups() {
      return this.computedItems.filter(item => !!item.children);
    },
    draggableTreeviewBind() {
      const bind = {
        expandIcon: this.expandIcon,
        class: 'pb-1',
      };
      if (!this.onlyChildrenDraggable) {
        bind.group = 'group';
      }
      return bind;
    },
  },
  methods: {
    // Nie chcę ale muszę. Komponent vuetify-draggable-treeview jest zbyt ograniczony. Inaczej się nie da :(
    openAllEmptyGroups() {
      const emptyGroups = this.computedGroups.filter(groups => groups.children && groups.children.length < 1);
      const emptyGroupsKeys = emptyGroups.map(group => group.uniqueTreeviewKey);
      for (const groupKey of emptyGroupsKeys) {
        this.$refs?.[`labelTreeviewElement${groupKey}false`][0]?.click();
      }
    },
    // Nie chcę ale muszę. Komponent vuetify-draggable-treeview jest zbyt ograniczony. Inaczej się nie da :(
    setClasses() {
      const refsKeys = JSON.parse(JSON.stringify(Object.keys(this.$refs)));
      this.computedItems.forEach(item => {
        let className = '';
        if (item.children && item.children.length > 0) {
          className = 'treeview-node-group';
        } else if (item.children && item.children.length < 1) {
          className = 'treeview-node-group-empty';
        } else {
          className = 'treeview-node-children';
        }
        const refKey = refsKeys.find(key => key === `prependTreeviewNode${item.uniqueTreeviewKey}`);
        if (!refKey) {
          return;
        }
        this.$refs[refKey][0].parentElement.parentElement.parentElement.className += ` ${className}`;
      });
    },
    selectElement(uid, value) {
      let parentItemIdx = undefined;
      const items = JSON.parse(JSON.stringify(this.computedItems));
      let item = items.find(item => item.uniqueTreeviewKey === uid);
      const type = item?.children ? 'group' : 'layer';
      let itemIdx = items.findIndex(item => item.uniqueTreeviewKey === uid);
      if (itemIdx < 0) {
        for (const group of items) {
          if (group.children) {
            itemIdx = group.children.findIndex(children => children.uniqueTreeviewKey === uid);
            if (itemIdx >= 0) {
              parentItemIdx = items.findIndex(item => item.uniqueTreeviewKey === group.uniqueTreeviewKey);
              item = group.children.find(group => group.uniqueTreeviewKey === uid);
              break;
            }
          }
        }
      }
      item.selectableValue = value;
      if (parentItemIdx || parentItemIdx === 0) {
        items[parentItemIdx].children[itemIdx] = item;
        const parentItemValue = items[parentItemIdx].children.find(children => children.selectableValue) ? true : false;
        items[parentItemIdx].selectableValue = parentItemValue;
      } else {
        items[itemIdx] = item;
        if (type === 'group') {
          items[itemIdx].children = items[itemIdx].children.map(children => {
            return { ...children, selectableValue: value };
          });
        }
      }
      this.$emit('select', { type, itemIdx, parentItemIdx, value });
      this.setItems(items);
    },
    setItems(v) {
      this.computedItems = v;
    },
  },
  watch: {
    computedItems: {
      deep: true,
      immediate: true,
      handler(nV, oV) {
        this.$nextTick(() => {
          this.setClasses();
          this.openAllEmptyGroups();
        });
        const groups = nV.filter(element => !!element?.children);
        for (const group of groups) {
          const groupElements = group.children;
          if (!groupElements.every(groupElement => !groupElement.children)) {
            this.$nextTick(() => {
              this.setItems(oV);
            });
            break;
          }
        }
      },
    },
  },
  created() {
    this.openAllEmptyGroups = debounce(this.openAllEmptyGroups, 500);
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .v-treeview {
    .sortable-chosen:not(.treeview-node-children):not(.treeview-node-group-empty):not(.treeview-node-group) {
      position: relative;
      left: -24px;
      .v-treeview-node__root .treeview-slot-append {
        position: relative;
        left: 24px;
      }
    }
  }
  .v-treeview-node__root {
    min-height: 0 !important;
  }
  .treeview-node-group-empty {
    min-height: 67px;
    padding-bottom: 5px;
    .v-treeview-node__children div {
      min-height: 30px;
    }
  }
  .v-treeview-node__children .sortable-chosen {
    background-color: white;
    padding-left: 24px;
    z-index: 1;
  }
  .no-elements {
    position: absolute;
    bottom: -25px;
    div {
      font-style: italic;
      font-size: 13px;
      opacity: 0.8;
      color: rgba(0, 0, 0, 0.6);
    }
  }
}
</style>
